// src/components/ContainerGrid.jsx
import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FaShoppingCart, FaExchangeAlt, FaDraftingCompass, FaHardHat,
  FaFileInvoiceDollar, FaTruck, FaCubes, FaLayerGroup, FaGripVertical,
} from "react-icons/fa";
import { supabase } from "../supabaseClient";
import "./ContainerGrid.css";

// ── Helper de normalização para busca (tolerante a acentos e maiúsculas) ──
const normalizarTexto = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
};

const getConsistentColor = (str) => {
  if (!str) return "#81C784";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 60%)`;
};

// ── Status do projeto: só renderiza se o campo existir no registro ──
function getEtapaBadge(etapa) {
  if (!etapa) return null;
  return { label: etapa };
}

function formatRelativo(dataStr) {
  if (!dataStr) return null;
  const data = new Date(dataStr);
  if (isNaN(data)) return null;
  const diffMin = Math.floor((Date.now() - data.getTime()) / 60000);
  if (diffMin < 1) return "Agora";
  if (diffMin < 60) return `Atualizado há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Atualizado há ${diffH} hora${diffH > 1 ? "s" : ""}`;
  const diffD = Math.floor(diffH / 24);
  return `Atualizado há ${diffD} dia${diffD > 1 ? "s" : ""}`;
}

// ── Ícone/cor por nome de setor (heurística simples) ──
const SETOR_VISUAL = [
  { match: /compr/i, icon: FaShoppingCart, bg: "#dcfce7", color: "#16a34a" },
  { match: /or[çc]amento|planej/i, icon: FaExchangeAlt, bg: "#dbeafe", color: "#2563eb" },
  { match: /arquitet/i, icon: FaDraftingCompass, bg: "#ede9fe", color: "#7c3aed" },
  { match: /obra/i, icon: FaHardHat, bg: "#fef3c7", color: "#d97706" },
  { match: /financ/i, icon: FaFileInvoiceDollar, bg: "#dcfce7", color: "#16a34a" },
  { match: /suprim/i, icon: FaTruck, bg: "#fee2e2", color: "#dc2626" },
];

function getSetorVisual(nome) {
  const found = SETOR_VISUAL.find((v) => v.match.test(nome || ""));
  if (found) return found;
  return { icon: FaCubes, bg: "#f1f5f9", color: "#475569" };
}

const WORKSPACE_VISUAL = { icon: FaLayerGroup, bg: "#eef2ff", color: "#4f46e5" };

// Troque por: import workspaceMascoteImg from "../assets/workspace-mascote.png";
// e use <img src={workspaceMascoteImg} .../> no lugar do ícone abaixo quando tiver o PNG da mascote.

// ── Card de Projeto ──
function SortableProjectCard({ proj, onProjectClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: proj.id });

  const inativo = proj.ativo === false;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  const badge = getEtapaBadge(proj.etapa_atual || proj.status);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`cx-card${inativo ? " cx-card--inativo" : ""}`}
      onClick={() => onProjectClick?.(proj)}
    >
      <div
        className="cx-card__cover"
        style={{
          backgroundImage: proj.photo_url ? `url(${proj.photo_url})` : "none",
          backgroundColor: proj.photo_url ? undefined : getConsistentColor(proj.id),
        }}
      >
        {inativo ? (
          <span className="cx-card__badge cx-card__badge--inativo">Inativo</span>
        ) : (
          badge && <span className="cx-card__badge">{badge.label}</span>
        )}

        {!proj.photo_url && (
          <span className="cx-card__initial">
            {proj.name?.charAt(0)?.toUpperCase() || "?"}
          </span>
        )}
      </div>

      <div className="cx-card__divider" />

      <div className="cx-card__body">
        <h3>{proj.name || "Projeto"}</h3>
      </div>
    </div>
  );
}

// ── Card de Setor ──
function SortableSetorCard({ setor, onSetorClick, onSetorAction, menuSetorAberto, setMenuSetorAberto }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: setor.id });
  const { icon: Icon, bg, color } = getSetorVisual(setor.name);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="cx-setor-card" onClick={() => onSetorClick?.(setor)}>
      <div
        className="cx-setor-card__menu-trigger"
        onClick={(e) => { e.stopPropagation(); setMenuSetorAberto?.(menuSetorAberto === setor.id ? null : setor.id); }}
      >
        ⋯
      </div>

      {menuSetorAberto === setor.id && (
        <div className="setor-actions-menu">
          <button onClick={(e) => { e.stopPropagation(); onSetorAction?.("verPerfil", setor); setMenuSetorAberto?.(null); }}>
            Ver perfil
          </button>
          <button onClick={(e) => { e.stopPropagation(); onSetorAction?.("delete", setor); setMenuSetorAberto?.(null); }}>
            Excluir
          </button>
        </div>
      )}

      <div {...listeners} className="cx-setor-card__content">
        <div className="cx-setor-card__icon" style={{ background: bg, color }}>
          {setor.photo_url ? <img src={setor.photo_url} alt={setor.name} /> : <Icon size={16} />}
        </div>
        <div className="cx-setor-card__text">
          <h3>{setor.name || "Setor"}</h3>
        </div>
      </div>
    </div>
  );
}

// ── Card de Workspace (pill branco: mascote à esquerda + nome em negrito à direita) ──
function SortableWorkspaceCard({ workspace, onWorkspaceClick, onWorkspaceAction, menuWorkspaceAberto, setMenuWorkspaceAberto }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: workspace.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="cx-workspace-card" onClick={() => onWorkspaceClick?.(workspace)}>
      <div
        className="cx-workspace-card__menu-trigger"
        onClick={(e) => { e.stopPropagation(); setMenuWorkspaceAberto?.(menuWorkspaceAberto === workspace.id ? null : workspace.id); }}
      >
        ⋯
      </div>

      {menuWorkspaceAberto === workspace.id && (
        <div className="setor-actions-menu">
          <button onClick={(e) => { e.stopPropagation(); onWorkspaceAction?.("verPerfil", workspace); setMenuWorkspaceAberto?.(null); }}>
            Ver perfil
          </button>
          <button onClick={(e) => { e.stopPropagation(); onWorkspaceAction?.("editar", workspace); setMenuWorkspaceAberto?.(null); }}>
            Editar
          </button>
          <button onClick={(e) => { e.stopPropagation(); onWorkspaceAction?.("delete", workspace); setMenuWorkspaceAberto?.(null); }}>
            Excluir
          </button>
        </div>
      )}

      <div {...listeners} className="cx-workspace-card__inner">
        <div className="cx-workspace-card__image">
          {workspace.photo_url ? (
            <img src={workspace.photo_url} alt={workspace.name} />
          ) : (
            <div className="cx-workspace-card__image-fallback" style={{ background: WORKSPACE_VISUAL.bg, color: WORKSPACE_VISUAL.color }}>
              <WORKSPACE_VISUAL.icon size={22} />
            </div>
          )}
        </div>
        <div className="cx-workspace-card__text">
          <h3>{workspace.name || "Workspace"}</h3>
        </div>
      </div>
    </div>
  );
}

// ── Header de seção arrastável (define a ordem Projetos/Setores/Workspaces) ──
function SortableSectionWrapper({ id, label, children, coluna = false, widthPct }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    ...(coluna ? { width: `${widthPct}%`, flex: `0 0 ${widthPct}%` } : {}),
  };

  return (
    <div ref={setNodeRef} style={style} className={`cx-section${coluna ? " cx-section--coluna" : ""}`}>
      <div className="cx-section__header" {...attributes}>
        <span className="cx-section__drag" {...listeners} title="Arraste para reordenar a seção">
          <FaGripVertical size={12} />
        </span>
        <h2>{label}</h2>
      </div>
      <div className="cx-section__body">{children}</div>
    </div>
  );
}

// ── Barra de redimensionamento entre duas colunas ──
function ColumnResizer({ leftKey, rightKey, containerRef, setColWidths, onCommit }) {
  const draggingRef = React.useRef(false);
  const startRef = React.useRef({ x: 0, leftW: 0, rightW: 0, containerWidth: 0 });

  const onMouseMove = (e) => {
    if (!draggingRef.current) return;
    const { x, leftW, rightW, containerWidth } = startRef.current;
    const deltaPct = ((e.clientX - x) / containerWidth) * 100;
    const MIN = 14;
    let newLeft = leftW + deltaPct;
    let newRight = rightW - deltaPct;
    if (newLeft < MIN) { newRight -= (MIN - newLeft); newLeft = MIN; }
    if (newRight < MIN) { newLeft -= (MIN - newRight); newRight = MIN; }
    setColWidths((prev) => ({ ...prev, [leftKey]: newLeft, [rightKey]: newRight }));
  };

  const onMouseUp = () => {
    draggingRef.current = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    setColWidths((current) => {
      onCommit?.(current);
      return current;
    });
  };

  const onMouseDown = (e, leftW, rightW) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    startRef.current = {
      x: e.clientX,
      leftW,
      rightW,
      containerWidth: containerRef.current?.getBoundingClientRect().width || 1,
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  return (
    <div
      className="cx-column-resizer"
      onMouseDown={(e) => {
        setColWidths((current) => {
          onMouseDown(e, current[leftKey], current[rightKey]);
          return current;
        });
      }}
    />
  );
}

const SECTION_LABELS = {
  projetos: "Projetos",
  setores: "Setores",
  workspaces: "Workspaces",
};

const DEFAULT_SECTION_ORDER = ["projetos", "setores", "workspaces"];
const DEFAULT_COLUMN_WIDTHS = { projetos: 33.34, setores: 33.33, workspaces: 33.33 };

export default function ContainerGrid({
  projects = [],
  setores = [],
  workspaces = [],
  searchTerm = "",
  onProjectClick,
  onSetorClick,
  onWorkspaceClick,
  onSetorAction,
  onWorkspaceAction,
  onViewProjectDetails,
  menuSetorAberto,
  setMenuSetorAberto,
  menuWorkspaceAberto,
  setMenuWorkspaceAberto,
  containerId,
  currentUserId,
  onReorderProjects,
  onReorderSetores,
  onReorderWorkspaces,
  sectionOrder = DEFAULT_SECTION_ORDER,
  onReorderSections,
  layoutModo = "linhas",
  columnWidths = DEFAULT_COLUMN_WIDTHS,
  onResizeColumns,
}) {
  const [projetosFiltrados, setProjetosFiltrados] = useState([]);
  const [setoresFiltrados, setSetoresFiltrados] = useState([]);
  const [workspacesFiltrados, setWorkspacesFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [colWidths, setColWidths] = useState(columnWidths);
  const columnsContainerRef = React.useRef(null);

  useEffect(() => {
    setColWidths(columnWidths);
  }, [columnWidths]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const filtrarPorPermissoes = async () => {
      if (!currentUserId || !containerId) {
        setProjetosFiltrados([]);
        setSetoresFiltrados([]);
        setWorkspacesFiltrados([]);
        setLoading(false);
        return;
      }

      try {
        if (currentUserId === containerId) {
          setProjetosFiltrados(projects);
          setSetoresFiltrados(setores);
          setWorkspacesFiltrados(workspaces);
          setLoading(false);
          return;
        }

        const { data: permissoes, error } = await supabase
          .from("permissoes_colaboradores")
          .select("projeto_id, setor_id, workspace_id")
          .eq("colaborador_id", currentUserId)
          .eq("container_id", containerId);

        if (error) {
          console.error("Erro ao buscar permissões:", error);
          setProjetosFiltrados([]);
          setSetoresFiltrados([]);
          setWorkspacesFiltrados([]);
          setLoading(false);
          return;
        }

        const projetosPermitidos = permissoes.filter((p) => p.projeto_id).map((p) => p.projeto_id);
        const setoresPermitidos = permissoes.filter((p) => p.setor_id).map((p) => p.setor_id);
        const workspacesPermitidos = permissoes.filter((p) => p.workspace_id).map((p) => p.workspace_id);

        setProjetosFiltrados(projects.filter((p) => projetosPermitidos.includes(p.id)));
        setSetoresFiltrados(setores.filter((s) => setoresPermitidos.includes(s.id)));
        setWorkspacesFiltrados(workspaces.filter((w) => workspacesPermitidos.includes(w.id)));
      } catch (err) {
        console.error("Erro ao filtrar permissões:", err);
        setProjetosFiltrados([]);
        setSetoresFiltrados([]);
        setWorkspacesFiltrados([]);
      } finally {
        setLoading(false);
      }
    };

    filtrarPorPermissoes();
  }, [projects, setores, workspaces, currentUserId, containerId]);

  // ── Aplica busca por texto sobre as listas já filtradas por permissão ──
  const termoBusca = normalizarTexto(searchTerm);

  const projetosVisiveis = termoBusca
    ? projetosFiltrados.filter((p) => normalizarTexto(p.name).includes(termoBusca))
    : projetosFiltrados;

  const setoresVisiveis = termoBusca
    ? setoresFiltrados.filter((s) => normalizarTexto(s.name).includes(termoBusca))
    : setoresFiltrados;

  const workspacesVisiveis = termoBusca
    ? workspacesFiltrados.filter((w) => normalizarTexto(w.name).includes(termoBusca))
    : workspacesFiltrados;

  const handleDragEndItens = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const isProject = projetosFiltrados.some((p) => p.id === active.id);
    const isSetor = !isProject && setoresFiltrados.some((s) => s.id === active.id);

    if (isProject) {
      const oldIndex = projetosFiltrados.findIndex((p) => p.id === active.id);
      const newIndex = projetosFiltrados.findIndex((p) => p.id === over.id);
      const newOrder = arrayMove(projetosFiltrados, oldIndex, newIndex);
      setProjetosFiltrados(newOrder);
      onReorderProjects?.(newOrder);
    } else if (isSetor) {
      const oldIndex = setoresFiltrados.findIndex((s) => s.id === active.id);
      const newIndex = setoresFiltrados.findIndex((s) => s.id === over.id);
      const newOrder = arrayMove(setoresFiltrados, oldIndex, newIndex);
      setSetoresFiltrados(newOrder);
      onReorderSetores?.(newOrder);
    } else {
      const oldIndex = workspacesFiltrados.findIndex((w) => w.id === active.id);
      const newIndex = workspacesFiltrados.findIndex((w) => w.id === over.id);
      const newOrder = arrayMove(workspacesFiltrados, oldIndex, newIndex);
      setWorkspacesFiltrados(newOrder);
      onReorderWorkspaces?.(newOrder);
    }
  };

  // ── Reordenar as SEÇÕES (Projetos / Setores / Workspaces) ──
  const visibleSectionOrder = sectionOrder.filter((key) => {
    if (key === "projetos") return projetosVisiveis.length > 0;
    if (key === "setores") return setoresVisiveis.length > 0;
    if (key === "workspaces") return workspacesVisiveis.length > 0;
    return false;
  });

  const handleDragEndSecoes = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sectionOrder.indexOf(active.id);
    const newIndex = sectionOrder.indexOf(over.id);
    const newOrder = arrayMove(sectionOrder, oldIndex, newIndex);
    onReorderSections?.(newOrder);
  };

  if (loading) return <p className="no-projects">Carregando...</p>;

  const temConteudo = visibleSectionOrder.length > 0;
  if (!temConteudo) {
    return (
      <p className="no-projects">
        {termoBusca ? "Nenhum resultado encontrado para sua busca." : "Tudo calmo por aqui ainda..."}
      </p>
    );
  }

  const emColuna = layoutModo === "colunas";
  const gridModifier = emColuna ? " cx-grid--coluna" : "";

  const renderSecaoConteudo = (key) => {
    if (key === "projetos") {
      return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndItens}>
          <SortableContext items={projetosVisiveis.map((p) => p.id)} strategy={emColuna ? verticalListSortingStrategy : horizontalListSortingStrategy}>
            <div className={`cx-grid${gridModifier}`}>
              {projetosVisiveis.map((proj) => (
                <SortableProjectCard key={proj.id} proj={proj} onProjectClick={onProjectClick} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      );
    }

    if (key === "setores") {
      return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndItens}>
          <SortableContext items={setoresVisiveis.map((s) => s.id)} strategy={emColuna ? verticalListSortingStrategy : horizontalListSortingStrategy}>
            <div className={`cx-grid cx-grid--setores${gridModifier}`}>
              {setoresVisiveis.map((setor) => (
                <SortableSetorCard
                  key={setor.id}
                  setor={setor}
                  onSetorClick={onSetorClick}
                  onSetorAction={onSetorAction}
                  menuSetorAberto={menuSetorAberto}
                  setMenuSetorAberto={setMenuSetorAberto}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      );
    }

    if (key === "workspaces") {
      return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndItens}>
          <SortableContext items={workspacesVisiveis.map((w) => w.id)} strategy={emColuna ? verticalListSortingStrategy : horizontalListSortingStrategy}>
            <div className={`cx-grid cx-grid--setores${gridModifier}`}>
              {workspacesVisiveis.map((workspace) => (
                <SortableWorkspaceCard
                  key={workspace.id}
                  workspace={workspace}
                  onWorkspaceClick={onWorkspaceClick}
                  onWorkspaceAction={onWorkspaceAction}
                  menuWorkspaceAberto={menuWorkspaceAberto}
                  setMenuWorkspaceAberto={setMenuWorkspaceAberto}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      );
    }

    return null;
  };

  // ── Modo LINHAS: seções empilhadas, uma abaixo da outra ──
  if (!emColuna) {
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndSecoes}>
        <SortableContext items={visibleSectionOrder} strategy={verticalListSortingStrategy}>
          {visibleSectionOrder.map((key) => (
            <SortableSectionWrapper key={key} id={key} label={SECTION_LABELS[key]}>
              {renderSecaoConteudo(key)}
            </SortableSectionWrapper>
          ))}
        </SortableContext>
      </DndContext>
    );
  }

  // ── Modo COLUNAS: seções lado a lado, largura ajustável por arraste ──
  const somaAtual = visibleSectionOrder.reduce((acc, key) => acc + (colWidths[key] ?? 0), 0) || 1;
  const larguraNormalizada = visibleSectionOrder.reduce((acc, key) => {
    acc[key] = ((colWidths[key] ?? 0) / somaAtual) * 100;
    return acc;
  }, {});

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndSecoes}>
      <SortableContext items={visibleSectionOrder} strategy={horizontalListSortingStrategy}>
        <div className="cx-columns-wrapper" ref={columnsContainerRef}>
          {visibleSectionOrder.map((key, idx) => (
            <React.Fragment key={key}>
              <SortableSectionWrapper id={key} label={SECTION_LABELS[key]} coluna widthPct={larguraNormalizada[key]}>
                {renderSecaoConteudo(key)}
              </SortableSectionWrapper>
              {idx < visibleSectionOrder.length - 1 && (
                <ColumnResizer
                  leftKey={key}
                  rightKey={visibleSectionOrder[idx + 1]}
                  containerRef={columnsContainerRef}
                  setColWidths={setColWidths}
                  onCommit={(finalWidths) => onResizeColumns?.(finalWidths)}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}